import { APP_INITIALIZER, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as SentryCapacitor from '@sentry/capacitor';
import { browserTracingIntegration, replayIntegration } from '@sentry/capacitor';
import { App } from '@capacitor/app';

export interface MonitoringModuleConfig {
  appName: string;
  sentryDsn: string;
  appVersion: string;
  appEnvironment: string;
  enable: boolean;
  sentryType: 'capacitor';
}

// Production API URLs
const mrfApiUrlsRegex = /https:\/\/app-mrf-prod-westeurope-api\.azurewebsites\.net\/api\//;
const mceApiUrlsRegex = /https:\/\/app-mce-prod-westeurope-api\.azurewebsites\.net\/api\//;
const fmaApiUrlsRegex = /https:\/\/app-fma-prod-westeurope-api\.azurewebsites\.net\/api\//;
const refillApiUrlsRegex = /https:\/\/app-refill-prod-westeurope-api\.azurewebsites\.net\/api\//;
const adminApiUrlsRegex = /https:\/\/app-admin-prod-westeurope-api\.azurewebsites\.net\/api\//;

// Staging API URLs
const mrfApiStagingUrlsRegex = /https:\/\/maurelli-mrf-api-staging\.zupit\.software\/api\//;
const mceApiStagingUrlsRegex = /https:\/\/maurelli-mce-api-staging\.zupit\.software\/api\//;
const fmaApiStagingUrlsRegex = /https:\/\/maurelli-fma-api-staging\.zupit\.software\/api\//;
const refillApiStagingUrlsRegex = /https:\/\/maurelli-refill-api-staging\.zupit\.software\/api\//;
const adminApiStagingUrlsRegex = /https:\/\/maurelli-admin-api-staging\.zupit\.software\/api\//;

@NgModule({
  imports: [CommonModule],
})
export class MonitoringModule {
  static forRoot(config: MonitoringModuleConfig) {
    return {
      ngModule: MonitoringModule,
      providers: [
        {
          provide: APP_INITIALIZER,
          useFactory: () => async () => {
            if (!config.enable) {
              return;
            }

            let appVersion = config.appVersion;
            try {
              const appInfo = await App.getInfo();
              appVersion = appInfo.version;
            } catch {
              // Fallback to config value (e.g. in browser during development)
            }

            const integrations = [
              browserTracingIntegration({}),
              replayIntegration({
                maskAllText: false,
                maskAllInputs: false,
                blockAllMedia: false,
                networkDetailAllowUrls: [
                  window.location.origin,
                  window.location.hostname,
                  mrfApiUrlsRegex,
                  mceApiUrlsRegex,
                  fmaApiUrlsRegex,
                  refillApiUrlsRegex,
                  adminApiUrlsRegex,
                  mrfApiStagingUrlsRegex,
                  mceApiStagingUrlsRegex,
                  fmaApiStagingUrlsRegex,
                  refillApiStagingUrlsRegex,
                  adminApiStagingUrlsRegex,
                ],
                networkCaptureBodies: true,
                networkRequestHeaders: ['Content-Type', 'Accept', 'Cache-Control', 'Carrier', 'Customer', 'X-Company', 'Company'],
                networkResponseHeaders: ['Content-Type', 'Cache-Control', 'Carrier', 'Customer', 'X-Company', 'Company'],
              }),
            ];
            const commonConfig = {
              dsn: config.sentryDsn,
              dist: appVersion,
              environment: config.appEnvironment,
              release: `${config.appName}@${appVersion}`,
              tracePropagationTargets: ['localhost', config.sentryDsn],
              tracesSampleRate: 1.0,
              replaysSessionSampleRate: 0.1,
              replaysOnErrorSampleRate: 1.0,
            };
            if (config.sentryType === 'capacitor') {
              SentryCapacitor.init({
                ...commonConfig,
                integrations: [...integrations],
                enableNative: true,
              });
            }
          },
          multi: true,
        },
      ],
    };
  }
}
